import { Box, Stack, TextField as MUITextField, TextFieldProps, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useField } from 'formik';

import useDebounceValue from 'modules/common/hooks/useDebounceEventHandler';

export type MUITextFieldProps = Omit<TextFieldProps, 'name'> & {
    name: string;
    defaultError?: string;
    isShowError?: boolean;
    isDebounced?: boolean;
    debounceTime?: number;
    type?: string;
    textOnDisabled?: boolean;
    /**
     * Optional callback to transform or validate the value before setting it in Formik.
     * Return the transformed value, or undefined to prevent the change.
     */
    onValueChange?: (value: string) => string | undefined;
    /**
     * Controls the label position:
     * - 'floating' (default): Label floats inside the input field (standard MUI behavior)
     * - 'external': Label appears as Typography above the input field
     */
    labelPosition?: 'floating' | 'external';
};

/**
 * A simple Formik wrapper for the MUI TextField.
 * All fields are overridable by props, but it is recommended to leave
 * handlers, `value`, `helperText`, and `error` alone
 *
 * @see {@link https://mui.com/material-ui/react-text-field/ MUI: TextField}
 * @see {@link https://formik.org/docs/api/useField Formik: useField}
 *
 * @param {Object} props
 * @param {string} props.name - unique field name in your form
 * @returns { React.FC }
 */
const TextField = ({
    name,
    defaultError,
    isShowError = true,
    isDebounced = false,
    debounceTime = 1000,
    type = 'text',
    textOnDisabled = false,
    onValueChange,
    labelPosition = 'floating',
    ...textFieldProps
}: MUITextFieldProps) => {
    const [field, meta, helpers] = useField(name);

    const isError = isShowError ? !!meta.error && meta.touched : false;
    const error = isError ? defaultError ?? meta.error : null;

    let onChange;
    let value;

    const [fieldValue, setFieldValue] = useDebounceValue(name, field.onChange, field.value, debounceTime);

    // Create a wrapped onChange that applies onValueChange transformation
    const createOnChange =
        (baseOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onValueChange) {
                const transformedValue = onValueChange(e.target.value);
                if (transformedValue !== undefined) {
                    helpers.setValue(transformedValue);
                }
            } else {
                baseOnChange(e);
            }
        };

    if (!isDebounced) {
        onChange = onValueChange ? createOnChange(field.onChange) : field.onChange;
        value = field.value;
    } else {
        const debouncedOnChange = (e: { target: { value: any } }) => {
            setFieldValue(e.target.value);
        };
        onChange = onValueChange ? createOnChange(debouncedOnChange) : debouncedOnChange;
        value = fieldValue;
    }

    useEffect(() => {
        if (isDebounced && !field.value && field.value !== fieldValue) {
            setFieldValue(field.value);
        }
    }, [isDebounced, field.value]);

    if (textOnDisabled && textFieldProps.disabled) {
        return (
            <Stack>
                <Typography variant="caption">{textFieldProps.label}</Typography>
                <Typography>{field.value || '---'}</Typography>
            </Stack>
        );
    }

    const textField = (
        <MUITextField
            {...field}
            onChange={onChange}
            value={value ?? ''}
            error={isError}
            type={type}
            {...textFieldProps}
            label={labelPosition === 'external' ? undefined : textFieldProps.label}
            helperText={error ?? textFieldProps.helperText}
        />
    );

    // When labelPosition is 'external', render label above the input
    if (labelPosition === 'external' && textFieldProps.label) {
        return (
            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    {textFieldProps.label}
                </Typography>
                {textField}
            </Box>
        );
    }

    return textField;
};

export default TextField;
